import { useQuery } from '@tanstack/react-query';
import { Button, EmptyState, ServerCard, Skeleton } from '@uplink/ui';
import { errorText, useTRPC } from '../api.ts';
import type { Nav } from '../nav.ts';
import { usePrefs } from '../prefs.tsx';
import { serverState } from '../servers-ui.tsx';

export function ServersScreen({ nav }: { nav: Nav }) {
  const trpc = useTRPC();
  const { t, money } = usePrefs();
  const list = useQuery({
    ...trpc.servers.list.queryOptions(),
    refetchInterval: (q) =>
      q.state.data?.some((s) => s.status === 'provisioning' || s.status === 'pending') ? 5000 : false,
  });
  const config = useQuery(trpc.config.queryOptions());

  return (
    <main className="ul-screen">
      <h1 className="ul-screen__title">{t.servers.title}</h1>

      {list.isPending ? (
        <Skeleton height={120} />
      ) : list.isError ? (
        <div className="ul-error" role="alert">
          {errorText(list.error)}
        </div>
      ) : list.data.length === 0 ? (
        <EmptyState
          icon="server"
          title={t.servers.emptyTitle}
          text={t.servers.emptyText(config.data?.serverFromCents ? money(config.data.serverFromCents) : null)}
        />
      ) : (
        <div className="ul-stack">
          {list.data.map((s) => {
            const st = serverState(s, t);
            return (
              <button
                key={s.id}
                type="button"
                className="ul-press"
                onClick={() => nav.push({ name: 'server', id: s.id })}
                aria-label={`${s.name}, ${st.label}`}
              >
                <ServerCard
                  name={s.name}
                  state={st.state}
                  region={s.city}
                  country={s.country}
                  // No IP copy button here: the whole card is a button; copying lives on the server screen
                  specs={[s.spec, s.ipv4 ?? '', st.state === 'ok' ? '' : st.label].filter(Boolean)}
                />
              </button>
            );
          })}
        </div>
      )}

      <div className="ul-row-actions">
        <Button size="l" block icon="plus" onClick={() => nav.push({ name: 'serverCreate' })}>
          {t.servers.create}
        </Button>
        <Button
          size="l"
          block
          variant="secondary"
          icon="shield"
          onClick={() => nav.push({ name: 'serverCreate', vpn: true })}
        >
          {t.servers.createVpn}
        </Button>
      </div>
    </main>
  );
}
